import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { useField } from 'formik';
import { InputAutoSuggestionWithFreeTextSettingsShape } from '@rabbit/elements/shared-types';

const dynamicStyles = {
  base: 'font-nunito bg-gray-50 text-base text-gray-900 border-[1px] rounded-lg border-gray-300 focus:border-primary-600 w-full px-4 py-3 placeholder-gray-400 !shadow-none !outline-none !ring-0',
  withAppend:
    'font-nunito border-[1px] border-gray-300 rounded-none rounded-tr-lg rounded-br-lg',
  withPrepend:
    'font-nunito border-[1px] border-gray-300 rounded-none rounded-tl-lg rounded-bl-lg',
  error: 'bg-red-50 border-red-500 text-red-700 rounded-lg',
  disabled: 'border-gray-300 text-gray-400 opacity-60 rounded-lg',
  chat: '!bg-white h-[42px] rounded-lg',
};

type InputAutoSuggestionWithFreeTextProps = {
  name: string;
  settings: InputAutoSuggestionWithFreeTextSettingsShape;
  label?: string;
  description?: string;
  labelStyles?: string;
};

export function InputAutoSuggestionWithFreeText({
  name,
  settings,
  label,
  description,
  labelStyles,
}: InputAutoSuggestionWithFreeTextProps) {
  const {
    placeholder,
    disabled,
    id,
    append,
    prepend,
    onFocus,
    suggestion,
    hint,
    ...rest
  } = settings;
  const [field, meta, helpers] = useField(name);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(field.value || '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Filter suggestions based on current word, or show all if input is empty and focused
  const filteredSuggestions = useMemo(() => {
    if (showSuggestions && !inputValue) return suggestion ?? [];
    if (!inputValue) return [];
    return (suggestion ?? []).filter((s) =>
      s.toLowerCase().startsWith(inputValue.toLowerCase())
    );
  }, [inputValue, suggestion, showSuggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    helpers.setValue(e.target.value);
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = (s: string) => {
    setInputValue(s);
    helpers.setValue(s);
    setShowSuggestions(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => setShowSuggestions(false), 100); // Delay to allow click
    if (settings.onFocus) settings.onFocus(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setShowSuggestions(true);
    if (settings.onFocus) settings.onFocus(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setHighlightedIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
      e.preventDefault();
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      handleSuggestionClick(filteredSuggestions[highlightedIndex]);
      e.preventDefault();
    }
  };

  return (
    <div className="flex flex-col w-full">
      {label && (
        <label
          htmlFor={name}
          className={
            'font-nunito mb-2 text-base text-gray-900 flex items-center gap-2 ' +
            (labelStyles || '')
          }
        >
          {label}
        </label>
      )}
      {description && (
        <p className="font-nunito -mt-2 mb-2 text-base text-gray-500">
          {description}
        </p>
      )}
      <div className="relative w-full">
        <div className="flex">
          {append && (
            <span className="rounded-tl-lg rounded-bl-lg border border-r-0 border-gray-300 bg-gray-100 px-4 py-3">
              {append}
            </span>
          )}
          <input
            type="text"
            id={id}
            placeholder={placeholder}
            autoComplete="off"
            disabled={disabled}
            className={classNames(dynamicStyles.base, {
              [dynamicStyles.withPrepend]: prepend,
              [dynamicStyles.withAppend]: append,
              [dynamicStyles.error]: meta.error && meta.touched,
              [dynamicStyles.disabled]: disabled,
            })}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            {...rest}
          />
          {prepend && (
            <span className="rounded-tr-lg rounded-br-lg border border-l-0 border-gray-300 bg-gray-100 px-4 py-3">
              {prepend}
            </span>
          )}
        </div>
        {showSuggestions && filteredSuggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-lg max-h-60 overflow-auto">
            {filteredSuggestions.map((s, idx) => (
              <li
                key={s}
                className={classNames(
                  'cursor-pointer px-4 py-2 text-base font-nunito text-gray-900 hover:bg-primary-100',
                  { 'bg-primary-100': idx === highlightedIndex }
                )}
                onMouseDown={() => handleSuggestionClick(s)}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                <span className="font-bold">{s.substr(0, inputValue.length)}</span>
                {s.substr(inputValue.length)}
              </li>
            ))}
          </ul>
        )}
      </div>
      {meta.error && meta.touched && (
        <div className="font-nunito mt-2 flex items-start text-base text-red-500">
          <span className="ml-1">
            {typeof meta.error == 'object'
              ? (Object.values(meta.error)[0] as any)
              : meta.error}
          </span>
        </div>
      )}
      {hint && !meta.error && (
        <div className="font-nunito mt-2 text-base text-gray-500">
          {hint}
        </div>
      )}
    </div>
  );
}

export default InputAutoSuggestionWithFreeText;
